import React, { useState, useCallback } from "react";
import {
  CustomTextInput,
  CustomDropdown,
  CustomDatePicker,
  CustomTimePicker,
  CustomMonthPicker,
  CustomButton,
} from "../../components";
import styles from "./SampleForm.module.css";
import dayjs from "dayjs";

const SampleForm = () => {
  // Dropdown Options
  const [countryOptions, setCountryOptions] = useState([
    { value: "us", label: "United States" },
    { value: "uk", label: "United Kingdom" },
    { value: "ca", label: "Canada" },
    { value: "au", label: "Australia" },
    { value: "de", label: "Germany" },
    { value: "fr", label: "France" },
    { value: "jp", label: "Japan" },
    { value: "in", label: "India" },
  ]);

  const genderOptions = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "other", label: "Other" },
    { value: "prefer-not", label: "Prefer not to say" },
  ];

  const [skillOptions, setSkillOptions] = useState([
    { value: "javascript", label: "JavaScript" },
    { value: "typescript", label: "TypeScript" },
    { value: "react", label: "React" },
    { value: "nodejs", label: "Node.js" },
    { value: "python", label: "Python" },
    { value: "java", label: "Java" },
    { value: "csharp", label: "C#" },
    { value: "go", label: "Go" },
  ]);

  const departmentOptions = [
    { value: "engineering", label: "Engineering" },
    { value: "design", label: "Design" },
    { value: "product", label: "Product Management" },
    { value: "marketing", label: "Marketing" },
    { value: "sales", label: "Sales" },
    { value: "hr", label: "Human Resources" },
  ];

  // Form State
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    gender: null,
    country: null,
    skills: [],
    department: null,
    dateOfBirth: null,
    joiningDate: null,
    meetingTime: null,
    reportMonth: null,
  });

  // Error State
  const [errors, setErrors] = useState({});

  // Touched State
  const [touched, setTouched] = useState({});

  // Loading State for submit
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle text input change
  const handleInputChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle blur
  const handleBlur = (name) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateField(name, formData[name]);
  };

  // Validate single field
  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "firstName":
        if (!value || !value.trim()) {
          error = "First name is required";
        } else if (value.trim().length < 2) {
          error = "First name must be at least 2 characters";
        }
        break;

      case "lastName":
        if (!value || !value.trim()) {
          error = "Last name is required";
        } else if (value.trim().length < 2) {
          error = "Last name must be at least 2 characters";
        }
        break;

      case "email":
        if (!value || !value.trim()) {
          error = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = "Please enter a valid email address";
        }
        break;

      case "phone":
        if (!value || !value.trim()) {
          error = "Phone number is required";
        } else if (!/^\d{10}$/.test(value.replace(/\D/g, ""))) {
          error = "Please enter a valid 10-digit phone number";
        }
        break;

      case "gender":
        if (!value) {
          error = "Please select your gender";
        }
        break;

      case "country":
        if (!value) {
          error = "Please select your country";
        }
        break;

      case "skills":
        if (!value || value.length === 0) {
          error = "Please select at least one skill";
        } else if (value.length > 5) {
          error = "You can select maximum 5 skills";
        }
        break;

      case "department":
        if (!value) {
          error = "Please select a department";
        }
        break;

      case "dateOfBirth":
        if (!value) {
          error = "Date of birth is required";
        }
        break;

      case "joiningDate":
        if (!value) {
          error = "Joining date is required";
        }
        break;

      case "meetingTime":
        if (!value) {
          error = "Meeting time is required";
        }
        break;

      case "reportMonth":
        if (!value) {
          error = "Report month is required";
        }
        break;

      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
    return error;
  };

  // Validate entire form
  const validateForm = () => {
    const fields = [
      "firstName",
      "lastName",
      "email",
      "phone",
      "gender",
      "country",
      "skills",
      "department",
      "dateOfBirth",
      "joiningDate",
      "meetingTime",
      "reportMonth",
    ];

    let isValid = true;
    const newErrors = {};
    const allTouched = {};

    fields.forEach((field) => {
      allTouched[field] = true;
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setTouched(allTouched);
    setErrors(newErrors);
    return isValid;
  };

  // Handle create new skill
  const handleCreateSkill = useCallback((inputValue) => {
    const newOption = {
      value: inputValue.toLowerCase().replace(/\s+/g, "-"),
      label: inputValue,
    };
    setSkillOptions((prev) => [...prev, newOption]);
    setFormData((prev) => ({
      ...prev,
      skills: [...prev.skills, newOption],
    }));
  }, []);

  // Handle create new country
  const handleCreateCountry = useCallback((inputValue) => {
    const newOption = {
      value: inputValue.toLowerCase().replace(/\s+/g, "-"),
      label: inputValue,
    };
    setCountryOptions((prev) => [...prev, newOption]);
    setFormData((prev) => ({
      ...prev,
      country: newOption,
    }));
  }, []);

  // Handle form submit
  const handleSubmit = (e) => {
    e?.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);

      // Format the data for logging
      const submittedData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        gender: formData.gender?.value || null,
        genderLabel: formData.gender?.label || null,
        country: formData.country?.value || null,
        countryLabel: formData.country?.label || null,
        skills: formData.skills.map((s) => s.value),
        skillLabels: formData.skills.map((s) => s.label),
        department: formData.department?.value || null,
        departmentLabel: formData.department?.label || null,
        dateOfBirth: formData.dateOfBirth
          ? dayjs(formData.dateOfBirth).format("DD/MM/YYYY")
          : "",
        joiningDate: formData.joiningDate
          ? dayjs(formData.joiningDate).format("DD/MM/YYYY")
          : "",
        meetingTime: formData.meetingTime
          ? dayjs(formData.meetingTime).format("HH:mm:ss")
          : "",
        reportMonth: formData.reportMonth
          ? dayjs(formData.reportMonth).format("MMMM")
          : "",
      };

      // Simulate API call
      setTimeout(() => {
        console.log("=".repeat(60));
        console.log("FORM SUBMITTED SUCCESSFULLY");
        console.log("=".repeat(60));
        console.log("\n📋 FORMATTED DATA:");
        console.log(JSON.stringify(submittedData, null, 2));
        console.log("\n📦 RAW FORM STATE:");
        console.log(JSON.stringify(formData, null, 2));
        console.log("=".repeat(60));

        alert(
          "Form submitted successfully! Check the console for logged values."
        );
        setIsSubmitting(false);
      }, 1000);
    } else {
      console.log("Errors:", errors);
    }
  };

  // Handle form reset
  const handleReset = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      gender: null,
      country: null,
      skills: [],
      department: null,
      dateOfBirth: null,
      joiningDate: null,
      meetingTime: null,
      reportMonth: null,
    });
    setErrors({});
    setTouched({});
    console.log("🔄 Form has been reset");
  };

  return (
    <div className={styles.formContainer}>
      <div className={styles.formCard}>
        <h1 className={styles.formTitle}>Employee Registration Form</h1>
        <p className={styles.formSubtitle}>
          Please fill in all required fields
        </p>

        <form onSubmit={handleSubmit} noValidate>
          {/* Personal Information Section */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Personal Information</h2>

            <div className={styles.row}>
              <div className={styles.field}>
                <CustomTextInput
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                  onBlur={() => handleBlur("firstName")}
                  placeholder="Enter first name"
                  required
                  error={touched.firstName ? errors.firstName : ""}
                />
              </div>

              <div className={styles.field}>
                <CustomTextInput
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                  onBlur={() => handleBlur("lastName")}
                  placeholder="Enter last name"
                  required
                  error={touched.lastName ? errors.lastName : ""}
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <CustomTextInput
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  onBlur={() => handleBlur("email")}
                  placeholder="Enter email address"
                  required
                  error={touched.email ? errors.email : ""}
                />
              </div>

              <div className={styles.field}>
                <CustomTextInput
                  label="Phone Number"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  onBlur={() => handleBlur("phone")}
                  placeholder="Enter phone number"
                  required
                  error={touched.phone ? errors.phone : ""}
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <CustomDropdown
                  label="Gender"
                  name="gender"
                  value={formData.gender}
                  onChange={(value) => handleInputChange("gender", value)}
                  onBlur={() => handleBlur("gender")}
                  options={genderOptions}
                  placeholder="Select gender..."
                  required
                  isSearchable={false}
                  error={touched.gender ? errors.gender : ""}
                />
              </div>

              <div className={styles.field}>
                <CustomDatePicker
                  label="Date of Birth"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={(value) => handleInputChange("dateOfBirth", value)}
                  onBlur={() => handleBlur("dateOfBirth")}
                  placeholder="Select date of birth"
                  required
                  error={touched.dateOfBirth ? errors.dateOfBirth : ""}
                  size="sm"
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <CustomDropdown
                  label="Country"
                  name="country"
                  value={formData.country}
                  onChange={(value) => handleInputChange("country", value)}
                  onBlur={() => handleBlur("country")}
                  options={countryOptions}
                  placeholder="Select or create country..."
                  required
                  isSearchable
                  createOptionKey="create"
                  onCreateOption={handleCreateCountry}
                  createOptionLabel={(input) => `+ Add "${input}"`}
                  error={touched.country ? errors.country : ""}
                  helperText={
                    !errors.country && !touched.country
                      ? "Can't find your country? Type to create"
                      : ""
                  }
                />
              </div>
            </div>
          </div>

          {/* Professional Information Section */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Professional Information</h2>

            <div className={styles.row}>
              <div className={styles.field}>
                <CustomDropdown
                  label="Skills"
                  name="skills"
                  value={formData.skills}
                  onChange={(value) => handleInputChange("skills", value)}
                  onBlur={() => handleBlur("skills")}
                  options={skillOptions}
                  placeholder="Select your skills..."
                  required
                  isMulti
                  isSearchable
                  createOptionKey="create"
                  onCreateOption={handleCreateSkill}
                  createOptionLabel={(input) => `+ Add "${input}" as a skill`}
                  error={touched.skills ? errors.skills : ""}
                  helperText={
                    !errors.skills && !touched.skills
                      ? "Select up to 5 skills"
                      : ""
                  }
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <CustomDropdown
                  label="Department"
                  name="department"
                  value={formData.department}
                  onChange={(value) => handleInputChange("department", value)}
                  onBlur={() => handleBlur("department")}
                  options={departmentOptions}
                  placeholder="Select department..."
                  required
                  isSearchable
                  error={touched.department ? errors.department : ""}
                />
              </div>

              <div className={styles.field}>
                <CustomDatePicker
                  label="Joining Date"
                  name="joiningDate"
                  value={formData.joiningDate}
                  onChange={(value) => handleInputChange("joiningDate", value)}
                  onBlur={() => handleBlur("joiningDate")}
                  placeholder="Select joining date"
                  required
                  error={touched.joiningDate ? errors.joiningDate : ""}
                  size="sm"
                />
              </div>
            </div>
          </div>

          {/* Schedule Information Section */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Schedule Information</h2>

            <div className={styles.row}>
              <div className={styles.field}>
                <CustomTimePicker
                  label="Preferred Meeting Time"
                  format="hh:mm a"
                  name="meetingTime"
                  value={formData.meetingTime}
                  onChange={(value) => handleInputChange("meetingTime", value)}
                  onBlur={() => handleBlur("meetingTime")}
                  placeholder="Select meeting time"
                  required
                  error={touched.meetingTime ? errors.meetingTime : ""}
                />
              </div>

              <div className={styles.field}>
                <CustomMonthPicker
                  label="Report Month"
                  name="reportMonth"
                  value={formData.reportMonth}
                  onChange={(value) => handleInputChange("reportMonth", value)}
                  onBlur={() => handleBlur("reportMonth")}
                  placeholder="Select report month"
                  required
                  error={touched.reportMonth ? errors.reportMonth : ""}
                  size="sm"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className={styles.actions}>
            <CustomButton
              type="button"
              variant="secondary"
              onClick={handleReset}
              disabled={isSubmitting}
            >
              Reset Form
            </CustomButton>

            <CustomButton
              type="submit"
              variant="primary"
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Form"}
            </CustomButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SampleForm;
